/**
 * ShiftEditSheet — edit an existing shift from the #665 master grid:
 * times, job function, notes, the PERSON (re-assignment via PR-A's PATCH
 * staffUserId), and delete.
 *
 * Dirty-fields-only PATCH: the server treats `staffUserId` as a
 * re-assignment only when it actually DIFFERS from the current assignee —
 * an echoed unchanged id is ignored, so this is not load-bearing for
 * correctness. Sending only what changed keeps the reassignment semantics
 * explicit at the boundary (a body with staffUserId MEANS "move this
 * shift") and is what the suites pin.
 *
 * The 409s keep the Add-shift form's semantics (P2 — errors say how to
 * recover): `overlapping_shift` is physics, no override offered;
 * `time_off_conflict` is policy, and the manager may explicitly choose
 * "Move anyway" — a named decision, never silent.
 *
 * Principles served: P2 (recoverable conflicts, obvious next action),
 * P3 (names, never UUIDs), P5 (draft/published state shown honestly;
 * roster failure degrades to a no-reassignment sheet instead of an empty
 * select that reads as "nobody exists").
 */
import { useState } from 'react';
import {
  readSession,
  ShiftConflictError,
  STAFF_ROLES,
  STAFF_ROLE_LABELS,
  updateStaffShift,
  type AdminStaffUser,
  type StaffRole,
  type StaffShift,
} from '../../lib/api.js';
import { formatDayShort, formatTimeRange } from '../../lib/schedule-dates.js';
import { isDraftShift } from './WeekGrid.js';

type SheetConflict =
  | { kind: 'overlapping_shift'; message: string }
  | { kind: 'time_off_conflict'; message: string };

export interface ShiftEditSheetProps {
  readonly apiUrl: string;
  readonly shift: StaffShift;
  /** ACTIVE roster for the person picker; null = roster unavailable —
   *  the sheet stays usable, minus re-assignment (honest degradation). */
  readonly roster: ReadonlyArray<AdminStaffUser> | null;
  readonly onClose: () => void;
  /** The server's answer after a successful PATCH. */
  readonly onSaved: (updated: StaffShift) => void;
  /** Delete is the PAGE's flow (optimistic removal + restore on failure). */
  readonly onDelete: (shift: StaffShift) => void;
}

function staffLabel(s: AdminStaffUser): string {
  const name = `${s.first_name} ${s.last_name}`.trim();
  return name ? `${name} (${s.username})` : s.username;
}

export function ShiftEditSheet({
  apiUrl,
  shift,
  roster,
  onClose,
  onSaved,
  onDelete,
}: ShiftEditSheetProps): JSX.Element {
  const [staffId, setStaffId] = useState(shift.staff_user_id);
  const [start, setStart] = useState(shift.start_time);
  const [end, setEnd] = useState(shift.end_time);
  const [role, setRole] = useState<string>(String(shift.role));
  const [notes, setNotes] = useState(shift.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<SheetConflict | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The current assignee may be deactivated (their row still renders) —
  // keep them selectable so the sheet's value is never a lie, but only
  // ACTIVE roster members are offered as re-assignment targets.
  const options: AdminStaffUser[] = roster ? [...roster] : [];
  if (roster && !roster.some((s) => s.id === shift.staff_user_id)) {
    options.unshift({
      id: shift.staff_user_id,
      username: shift.staff_name,
      first_name: shift.staff_name,
      last_name: '',
      role: String(shift.role),
      is_active: false,
      created_at: '',
    } as AdminStaffUser);
  }

  /** ONLY the fields that differ from the loaded shift ride the PATCH. */
  function dirtyBody(override: boolean): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (staffId !== shift.staff_user_id) body.staffUserId = staffId;
    if (start !== shift.start_time) body.startTime = start;
    if (end !== shift.end_time) body.endTime = end;
    if (role !== String(shift.role)) body.role = role;
    const normalizedNotes = notes.trim() === '' ? null : notes;
    if (normalizedNotes !== (shift.notes ?? null)) body.notes = normalizedNotes;
    if (override) body.override = true;
    return body;
  }

  const save = async (override: boolean): Promise<void> => {
    const { token } = readSession();
    if (!token) return;
    const body = dirtyBody(override);
    const changed = Object.keys(body).filter((k) => k !== 'override');
    if (changed.length === 0) {
      // Nothing changed — closing IS the save (no no-op PATCH).
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await updateStaffShift(apiUrl, token, shift.id, body);
      setConflict(null);
      onSaved(result.shift);
    } catch (err) {
      if (err instanceof ShiftConflictError) {
        setConflict(
          err.conflict.kind === 'overlapping_shift'
            ? {
                kind: 'overlapping_shift',
                message: `${err.message} Change the hours, or edit the other shift first.`,
              }
            : {
                kind: 'time_off_conflict',
                message: `${err.message} Move the shift anyway?`,
              },
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to update shift');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="ferro-form ferro-shift-edit"
      aria-label="Edit shift"
      data-testid="shift-edit-sheet"
      onSubmit={(e) => {
        e.preventDefault();
        void save(false);
      }}
    >
      <div className="ferro-shift-edit__head">
        <strong>
          Edit shift — {formatDayShort(shift.date)},{' '}
          {formatTimeRange(shift.start_time, shift.end_time)}
        </strong>
        {/* Honest lifecycle state: a draft says so (P5). */}
        <span
          className={`ferro-shift-edit__state${
            isDraftShift(shift) ? ' ferro-shift-edit__state--draft' : ''
          }`}
        >
          {isDraftShift(shift) ? 'Draft — staff can’t see this yet' : 'Published'}
        </span>
      </div>

      <div className="ferro-field">
        <span className="ferro-field__label">Person</span>
        {roster ? (
          <select
            className="ferro-field__input"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            aria-label="Person"
            data-testid="edit-staff-select"
          >
            {options.map((s) => (
              <option key={s.id} value={s.id}>
                {staffLabel(s)}
              </option>
            ))}
          </select>
        ) : (
          // Roster unavailable: the shift stays editable, re-assignment
          // honestly doesn't (never an empty select that reads as
          // "nobody exists" — the #278 posture).
          <p className="ferro-muted" data-testid="edit-roster-unavailable">
            {shift.staff_name} — we couldn’t load the roster, so moving this
            shift to someone else isn’t available right now.
          </p>
        )}
      </div>

      <label className="ferro-field">
        <span className="ferro-field__label">Start</span>
        <input
          type="time"
          className="ferro-field__input"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          required
        />
      </label>
      <label className="ferro-field">
        <span className="ferro-field__label">End</span>
        <input
          type="time"
          className="ferro-field__input"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          required
        />
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Job for this shift</span>
        <select
          className="ferro-field__input"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="edit-role-select"
        >
          {/* An out-of-list value (server ahead of client) stays selectable
              rather than being silently rewritten. */}
          {!STAFF_ROLES.includes(role as StaffRole) ? (
            <option value={role}>{role}</option>
          ) : null}
          {STAFF_ROLES.map((r) => (
            <option key={r} value={r}>
              {STAFF_ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Notes</span>
        <textarea
          className="ferro-field__input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={1000}
          data-testid="edit-notes"
        />
      </label>

      {error ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{error}</p>
        </div>
      ) : null}

      {conflict?.kind === 'overlapping_shift' ? (
        <div
          className="ferro-alert ferro-alert--error"
          role="alert"
          data-testid="edit-overlap-error"
        >
          {conflict.message}
        </div>
      ) : null}

      {conflict?.kind === 'time_off_conflict' ? (
        <div
          className="ferro-alert ferro-alert--warning"
          role="alert"
          data-testid="edit-timeoff-conflict"
        >
          <p>{conflict.message}</p>
          <div className="ferro-form__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--outline"
              disabled={submitting}
              onClick={() => void save(true)}
              data-testid="edit-timeoff-override"
            >
              Move anyway
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setConflict(null)}
            >
              Keep as is
            </button>
          </div>
        </div>
      ) : null}

      <div className="ferro-form__actions">
        <button
          type="submit"
          className="ferro-btn ferro-btn--primary"
          disabled={submitting}
          data-testid="edit-save"
        >
          {submitting ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-shift-edit__delete"
          disabled={submitting}
          onClick={() => onDelete(shift)}
          data-testid="edit-delete"
        >
          Delete shift
        </button>
      </div>
    </form>
  );
}
